export type GlobalSearchResultKind =
  | 'products'
  | 'orders'
  | 'customers'
  | 'categories'
  | 'collections';

const ARABIC_DIACRITICS = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED]/g;
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

/** Normalize Arabic spelling and numeral variants for dashboard-side matching. */
export function normalizeGlobalSearchText(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(ARABIC_DIACRITICS, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ـ/g, '')
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/\s+/g, ' ')
    .trim();
}

export function detectGlobalSearchIntent(value: string): GlobalSearchResultKind | 'general' {
  const term = normalizeGlobalSearchText(value);
  const digits = term.replace(/\D/g, '');

  if (/^#\s*\d+/.test(term) || /^طلب\s*#?\s*\d+/.test(term)) return 'orders';
  if (term.includes('@')) return 'customers';
  if (
    term.startsWith('+')
    || /^0\d{8,}$/.test(term)
    || /^966\d{9}$/.test(term)
    || (/^[\d\s()-]+$/.test(term) && digits.length >= 7)
  ) return 'customers';
  if (
    /^sku\s*:/i.test(term)
    || (/^[a-z0-9_-]*\d[a-z0-9_-]*$/i.test(term) && /[a-z_-]/i.test(term))
  ) return 'products';
  return 'general';
}

const defaultGroupOrder: GlobalSearchResultKind[] = [
  'products',
  'orders',
  'customers',
  'categories',
  'collections',
];

export function getGlobalSearchGroupOrder(value: string): GlobalSearchResultKind[] {
  const intent = detectGlobalSearchIntent(value);
  if (intent === 'general') return defaultGroupOrder;
  return [intent, ...defaultGroupOrder.filter((kind) => kind !== intent)];
}
